import React, { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import api from '../../lib/api';

const inputCls = (err) => `w-full bg-transparent border-0 border-b ${err ? 'border-[#B42318]' : 'border-[#111111]/25'} px-0 py-3 text-base font-light text-[#111111] placeholder:text-[#6B6B6B]/60 focus:outline-none focus:border-[#111111] focus-visible:ring-0 transition-colors rounded-none`;

const Label = ({ id, f }) => (
  <label htmlFor={id} className={`block text-[11px] uppercase tracking-[0.22em] text-[#111111] mb-1 leading-relaxed ${f.hideLabel ? 'sr-only' : ''}`}>
    {f.label}{f.required && <span aria-hidden="true"> *</span>}
  </label>
);

const ErrorMsg = ({ id, msg }) => msg ? <p id={`${id}-err`} role="alert" className="mt-2 text-xs text-[#B42318]">{msg}</p> : null;

const Choices = ({ id, f, value, onChange, err, multi, tiles }) => {
  const selected = multi ? (value || []) : value;
  const toggle = (o) => onChange(multi ? (selected.includes(o) ? selected.filter((x) => x !== o) : [...selected, o]) : o);
  return (
    <fieldset aria-describedby={err ? `${id}-err` : undefined} aria-invalid={!!err} data-testid={`field-${id}`}>
      <legend className={`text-[11px] uppercase tracking-[0.22em] text-[#111111] mb-4 leading-relaxed ${f.hideLabel ? 'sr-only' : ''}`}>
        {f.label}{f.required && <span aria-hidden="true"> *</span>}
      </legend>
      <div className={tiles ? (f.tilesClass || 'grid grid-cols-2 gap-3') : 'flex flex-wrap gap-2'}>
        {f.options.map((o, i) => {
          const on = multi ? selected.includes(o) : selected === o;
          return (
            <label key={o} className={`relative cursor-pointer border text-center transition-colors duration-300 ${tiles ? 'px-4 py-5 text-[11px] uppercase tracking-[0.22em] leading-[1.6] break-words flex items-center justify-center' : 'px-4 py-2.5 text-sm font-light'} ${on ? 'bg-[#111111] text-white border-[#111111]' : 'bg-white text-[#111111] border-[#111111]/20 hover:border-[#111111]'} has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-[#111111]`}>
              <input type={multi ? 'checkbox' : 'radio'} name={id} value={o} checked={on} onChange={() => toggle(o)} className="sr-only" data-testid={`${id}-opt-${i}`} />
              {o}
            </label>
          );
        })}
      </div>
      <ErrorMsg id={id} msg={err} />
    </fieldset>
  );
};

const FileField = ({ id, f, value, onChange, err }) => {
  const [busy, setBusy] = useState(false);
  const [upErr, setUpErr] = useState('');
  const files = value || [];
  const accept = f.videoOnly ? 'video/mp4,video/quicktime,video/webm' : f.video ? 'image/*,application/pdf,video/mp4,video/quicktime,video/webm' : 'image/*,application/pdf';
  const upload = async (list) => {
    const max = (t) => (t.startsWith('video/') ? 200 : 5);
    const notVideo = f.videoOnly ? list.filter((file) => !file.type.startsWith('video/')) : [];
    const tooBig = list.filter((file) => file.size > max(file.type) * 1024 * 1024);
    const msgs = [
      ...notVideo.map((x) => `Veuillez joindre une vidéo (MP4, MOV ou WEBM) : ${x.name}`),
      ...tooBig.map((x) => `Fichier trop volumineux : ${x.name} (${max(x.type)} Mo maximum pour ce type de fichier).`),
    ];
    list = list.filter((file) => !tooBig.includes(file) && !notVideo.includes(file));
    setUpErr(msgs.join(' '));
    if (!list.length) return;
    setBusy(true);
    const added = [];
    for (const file of list) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        if (f.videoOnly) fd.append('kind', 'video');
        const { data } = await api.post('/upload-public', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        added.push({ url: data.url, name: data.name });
      } catch (e) {
        msgs.push(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : `Échec de l’envoi : ${file.name}`);
      }
    }
    setUpErr(msgs.join(' '));
    onChange([...files, ...added]);
    setBusy(false);
  };
  return (
    <div data-testid={`field-${id}`}>
      <Label id={id} f={f} />
      <label htmlFor={id} className={`mt-3 inline-flex items-center gap-3 border border-[#111111] px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] text-[#111111] hover:bg-[#111111] hover:text-white transition-colors has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 ${busy ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}>
        Choisir {f.videoOnly ? 'une vidéo' : 'des fichiers'}
        <input id={id} type="file" multiple accept={accept} disabled={busy} aria-describedby={err ? `${id}-err` : undefined} aria-invalid={!!err}
          onChange={(e) => { upload([...e.target.files]); e.target.value = ''; }}
          className="sr-only" data-testid={`${id}-input`} />
      </label>
      {!files.length && !busy && <p className="mt-2 text-xs text-[#6B6B6B]">Aucun fichier sélectionné</p>}
      {busy && <p className="mt-2 text-xs text-[#6B6B6B] flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Envoi en cours…</p>}
      {files.map((x, i) => (
        <p key={x.url} className="mt-2 text-xs text-[#333333] flex items-center gap-2">
          {x.name}
          <button type="button" aria-label={`Retirer ${x.name}`} onClick={() => onChange(files.filter((_, j) => j !== i))}><X className="w-3 h-3" /></button>
        </p>
      ))}
      <ErrorMsg id={id} msg={[err, upErr].filter(Boolean).join(' ')} />
    </div>
  );
};

export const FormField = ({ id, f, value, onChange, err }) => {
  if (f.type === 'note') return <p className="text-xs italic font-light leading-[1.8] text-[#6B6B6B]">{f.label}</p>;
  if (f.type === 'universe') return <Choices id={id} f={f} value={value} onChange={onChange} err={err} tiles />;
  if (f.type === 'radio') return <Choices id={id} f={f} value={value} onChange={onChange} err={err} />;
  if (f.type === 'checkbox') return <Choices id={id} f={f} value={value} onChange={onChange} err={err} multi />;
  if (f.type === 'file') return <FileField id={id} f={f} value={value} onChange={onChange} err={err} />;
  if (f.type === 'consent') {
    return (
      <div data-testid={`field-${id}`}>
        <label className="flex items-start gap-3 cursor-pointer text-sm font-light leading-[1.7] text-[#333333]">
          <input id={id} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} aria-invalid={!!err} aria-describedby={err ? `${id}-err` : undefined} className="mt-1 w-4 h-4 accent-[#111111] shrink-0" data-testid={`${id}-input`} />
          <span>{f.label}{f.required && <span aria-hidden="true"> *</span>}</span>
        </label>
        <ErrorMsg id={id} msg={err} />
      </div>
    );
  }
  const common = { id, value: value || '', onChange: (e) => onChange(e.target.value), 'aria-invalid': !!err, 'aria-describedby': err ? `${id}-err` : undefined, 'aria-required': !!f.required, 'data-testid': `${id}-input` };
  return (
    <div data-testid={`field-${id}`}>
      <Label id={id} f={f} />
      {f.type === 'textarea'
        ? <textarea rows={4} {...common} className={`${inputCls(err)} resize-y min-h-[110px]`} />
        : <input type={f.input || 'text'} {...common} className={inputCls(err)} />}
      <ErrorMsg id={id} msg={err} />
    </div>
  );
};
